// src/components/PasskeySection.jsx
// 3.69.0 (owner, Oct 29): Security → Face ID & fingerprint. Turn the
// unlock on or off for this device, and see (and remove) every device
// that has it. Turning it on needs a sign-in with the password in the last
// 10 minutes, so it may ask "confirm it's you" first (ReauthBox).
import { useEffect, useState } from 'react';
import { useAuth, deviceLabel } from '../hooks/useAuth';
import { fetchMfaStatus } from '../utils/api';
import { addPasskey, removePasskeys, passkeyFor, forgetPasskey, passkeysPossible, unlockWord } from '../utils/passkeys';
import { ReauthBox } from './TwoFactorSetup';
import { dialogStyles as S } from './ProfileDialog';
import { timeZone } from '../utils/timeZone';

export default function PasskeySection({ notify }) {
  const { user, userInfo, getToken, viewAs } = useAuth();
  const word = unlockWord();
  const [list, setList]         = useState(null);   // the server's list
  const [days, setDays]         = useState(30);
  const [possible, setPossible] = useState(null);
  const [reauth, setReauth]     = useState(false);
  const [busy, setBusy]         = useState('');
  const [err, setErr]           = useState('');
  const [mine, setMine]         = useState(() => passkeyFor(user?.email));

  useEffect(() => {
    let off = false;
    fetchMfaStatus(getToken).then(r => { if (!off) { setList(r.passkeys || []); setDays(r.passkeyDays || 30); } }).catch(() => { if (!off) setList([]); });
    passkeysPossible().then(ok => { if (!off) setPossible(ok); });
    return () => { off = true; };
  }, [getToken]);

  // This device's passkey, as long as the server still has it
  const here = mine && (list || []).find(p => p.id === mine.id);
  const day = iso => new Date(iso).toLocaleDateString('en-US', { timeZone: timeZone(), month: 'short', day: 'numeric', year: 'numeric' });
  const expired = p => Date.parse(p.validUntil) < Date.now();

  async function turnOn(freshToken = false) {
    setErr(''); setBusy('on');
    try {
      const r = await addPasskey({ email: user.email, name: userInfo?.name || '', label: deviceLabel(),
        getToken: freshToken ? () => user.getIdToken(true) : getToken });
      const s = await fetchMfaStatus(getToken).catch(() => null);
      setList(s?.passkeys || [...(list || []), { id: r.id, label: deviceLabel(), createdAt: new Date().toISOString(), validUntil: r.validUntil }]);
      setMine(passkeyFor(user.email));
      notify({ tone: 'success', title: 'Unlock is on for this device', detail: 'Next time the portal locks, open it without your password.' });
    } catch (e) {
      if (e.code === 'REAUTH_REQUIRED') setReauth(true);
      else if (e.code !== 'CANCELLED') setErr(e.message);
    }
    setBusy('');
  }

  async function remove(id) {
    setErr(''); setBusy(id || 'all');
    try {
      await removePasskeys(id, getToken);
      if (!id || id === mine?.id) { forgetPasskey(user.email); setMine(null); }
      setList(l => (id ? l.filter(p => p.id !== id) : []));
      notify({ tone: 'success', title: id ? 'Removed' : 'Removed from all devices', detail: 'The password will be asked there when the portal locks.' });
    } catch (e) { setErr(e.message); }
    setBusy('');
  }

  return (
    <section>
      <p style={S.status}>
        <span style={{ ...S.pill, ...(here ? (expired(here) ? S.pillWarn : S.pillOn) : S.pillOff) }}>
          {here ? (expired(here) ? 'Needs password' : 'On') : 'Off'}
        </span>
        <span>
          With this on, the portal locks after an hour without use instead of signing you out, and you open it again
          with {word} instead of your portal password and code. After you sign out, you can sign in again with it too. Your portal
          password is still needed once every {days} days. Use it only on a device that's yours.
        </span>
      </p>

      {here && (
        <p style={{ ...S.note, marginTop: 0 }}>
          {expired(here)
            ? `It's been ${days} days since you last signed in with your password here. Sign in with it once and it carries on.`
            : `On for this device until ${day(here.validUntil)}. Each sign-in with your password here adds ${days} days.`}
        </p>
      )}

      {!here && !reauth && possible && !viewAs && (
        <button onClick={() => turnOn()} disabled={!!busy} style={S.saveBtn}>{busy === 'on' ? 'Follow the steps on your device…' : `Turn on ${word} for this device`}</button>
      )}
      {!here && possible === false && (
        <p style={{ ...S.note, marginTop: 0 }}>
          This device or browser can't do it. It needs a screen lock: Face ID, face unlock, a fingerprint, or a passcode, PIN or
          pattern on phones; Touch ID or the Mac password on Macs; Windows Hello (face, fingerprint or PIN) on Windows.
        </p>
      )}
      {reauth && (
        <ReauthBox text={`For your security, please confirm it's you, then we'll turn on ${word}.`}
          onConfirmed={() => { setReauth(false); turnOn(true); }} onCancel={() => setReauth(false)} />
      )}

      {(list || []).length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t-37474f)', marginBottom: 6 }}>Devices with it on</div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {list.map(p => (
              <li key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '8px 12px', border: '1px solid var(--c-eceff1)', borderRadius: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--t-37474f)', minWidth: 0 }}>
                  <strong>{p.label}</strong>{p.id === mine?.id ? ' · this device' : ''}
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--t-78909c)' }}>
                    Turned on {day(p.createdAt)} · {expired(p) ? 'needs your password' : `works until ${day(p.validUntil)}`}{p.lastUsedAt ? ` · last used ${day(p.lastUsedAt)}` : ''}
                  </span>
                </span>
                <button onClick={() => remove(p.id)} disabled={!!busy || !!viewAs} style={S.linkBtn}>{busy === p.id ? 'Removing…' : 'Remove'}</button>
              </li>
            ))}
          </ul>
          {list.length > 1 && <button onClick={() => remove(null)} disabled={!!busy || !!viewAs} style={{ ...S.linkBtn, marginTop: 6 }}>{busy === 'all' ? 'Removing…' : 'Remove from all devices'}</button>}
        </div>
      )}

      {err && <div style={{ ...S.error, marginTop: 10 }} role="alert">{err}</div>}
    </section>
  );
}
